<!-- 
Panel for displaying non-compliance history configuration for a levy collection.
-->
<template>
  <v-card flat>
    <v-card-text class="pr-2 py-0">
      <our-busy-indicator :show="state.loading"/>
      <our-data-container css="pb-0">
        <our-data-field label="Include sanctions" label-width="6" offset="0">
          {{ configuration.enableSanctions | yesNo }}
        </our-data-field>
        <our-data-field label="Include registration periods" label-width="6" offset="0">
          {{ configuration.enableShortRegistrationPeriodRiskFactor | yesNo }}
        </our-data-field>
        <our-data-field label="Include annual charge late payments" label-width="6" offset="0">
          {{ configuration.numberOfAnnualChargeLatePaymentYears | latePaymentYears }}
        </our-data-field>
        <our-data-field label="Include levy late payments" label-width="6" offset="0">
          {{ configuration.numberOfLtpsLatePaymentYears | latePaymentYears }}
        </our-data-field>
      </our-data-container>
      <template v-if="numberOfWeightings">
        <v-data-table 
          :headers="headers" 
          :items="weightings" 
          :total-items="numberOfWeightings" 
          hide-actions 
          class="risk-factor-table"
        >
          <template slot="items" slot-scope="props">
            <tr class="noselect">
              <td>
                {{ props.item.text }}
              </td>
              <td class="text-xs-center">
                {{ props.item.value | numericInput2 }}
              </td>
            </tr>
          </template>
        </v-data-table>
      </template>
    </v-card-text>
    <v-card-actions v-if="canEdit" class="justify-center">
      <v-btn @click="showEditDialog=true">
        <v-icon class="mr-2">edit</v-icon>
        Edit
      </v-btn>
    </v-card-actions>
    <non-compliance-history-configuration-dialog v-model="showEditDialog" :levy-collection-id="levyCollectionId"/>
  </v-card>
</template>

<script>
import lookupMixins from "@/mixins/lookup.mixins";
import filterMixins from "@/mixins/filter.mixins";
import * as claim from "@/store/claim-types";
import * as action from "@/store/levy-collections/action-types";
import NonComplianceHistoryConfigurationDialog from "@/components/levy-collection-setup/NonComplianceHistoryConfigurationDialog.vue";

export default {
  components: { NonComplianceHistoryConfigurationDialog },
  filters: {
    latePaymentYears(years) {
      switch (years) {
        case 0:
          return "No";
        case 1:
          return "From previous year only";
        default:
          return `From previous ${years} years`;
      }
    }
  },
  mixins: [filterMixins, lookupMixins],
  props: {
    levyCollectionId: {
      validator: prop => typeof prop === "number" || prop === null,
      required: true
    }
  },
  data() {
    return {
      headers: [
        { text: "Year", align: "left", sortable: false },
        { text: "Weighting", align: "center", sortable: false }
      ],
      showEditDialog: false
    };
  },
  computed: {
    state() {
      return this.$store.state.levyCollections.viewNonComplianceHistoryConfiguration;
    },
    configuration() {
      return this.state.data;
    },
    loaded() {
      return this.state.loaded;
    },
    canEdit() {
      const hasPermission = this.$store.getters.claims.includes(claim.LEVY_COLLECTION_CONFIGURE_RISK_FACTORS);
      const isAllowableStatus = this.doesLookupHaveAttribute(
        this.state.data.status,
        "CanEditRiskFactors",
        this.lookups.levyCollectionStatuses
      );
      return hasPermission && isAllowableStatus;
    },
    numberOfWeightings() {
      return Math.max(
        this.configuration.numberOfAnnualChargeLatePaymentYears,
        this.configuration.numberOfLtpsLatePaymentYears
      );
    },
    weightings() {
      const w = [];
      for (let i = 1; i <= this.numberOfWeightings; i++) {
        const suffix = i > 1 ? ` - ${i - 1}` : "";
        const field =
          i == 1 ? "latePaymentWeightingForPreviousYear" : `latePaymentWeightingForPreviousYearMinus${i - 1}`;
        w.push({ text: `Previous year${suffix}`, value: this.configuration[field] });
      }
      return w;
    }
  },
  watch: {
    levyCollectionId() {
      this.refresh();
    },
    loaded() {
      this.refresh();
    }
  },
  mounted() {
    this.refresh();
  },
  methods: {
    refresh() {
      const needsReloaded =
        (!this.state.loaded && !this.state.loading) || this.state.levyCollectionId != this.levyCollectionId;
      if (needsReloaded) {
        this.$store.dispatch(action.VIEW_NON_COMPLIANCE_HISTORY_CONFIGURATION, this.levyCollectionId);
      }
    },
    showWeighting(number) {
      return (
        this.configuration.numberOfAnnualChargeLatePaymentYears >= number ||
        this.configuration.numberOfLtpsLatePaymentYears >= number
      );
    }
  }
};
</script>
